import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import uniqBy from "lodash/uniqBy";

import { useDebounce } from "hooks/useDebounce";
import TagService from "services/Tag";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

// A module-level function keeps the `select` reference stable, so it re-runs
// only when the data changes.
const flattenTagPages = ({ pages }) =>
  uniqBy(
    pages.flatMap(page => page.data),
    "id"
  );

/**
 * The tags a user can apply to a `modelType`, loaded a page at a time as the
 * picker scrolls. `search` is the raw input value; the request follows it once
 * typing pauses. `enabled` keeps the request from going out until the picker
 * is opened. The result is an infinite query whose `data` is the flat tag list.
 */
export function useApplyTagOptions({
  modelType,
  search = "",
  includeDisabled = false,
  dialSessionId,
  enabled = true,
}) {
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);

  return useInfiniteQuery({
    queryKey: [
      "tags",
      "apply",
      modelType,
      debouncedSearch,
      includeDisabled,
      dialSessionId,
    ],
    queryFn: ({ pageParam }) =>
      TagService.getTagsPage({
        model: modelType,
        page: pageParam,
        perPage: PAGE_SIZE,
        search: debouncedSearch,
        includeDisabled,
        dialSessionId,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
    // The list stays on screen while a new search loads, instead of flashing
    // back to the skeleton on every pause in typing.
    placeholderData: keepPreviousData,
    select: flattenTagPages,
    enabled,
  });
}
